/**
 * AssumptionsPage – displays system rates, thresholds, and assumptions used in projections.
 * Single source for aged care, social security, tax, and asset/market assumptions.
 */
import React, { useState } from 'react';
import { ChevronLeft, ChevronDown } from 'lucide-react';
import { Card, GradientText, InfoIcon } from '../components/ui/index.tsx';
import { ratesReviewStatus } from '../../../shared/src/calculation/ratesReview.ts';
import { buildAssumptionSections } from '../features/advice/assumptionSections.ts';

export interface AssumptionsPageProps {
    setActivePage: (page: string) => void;
}

const AssumptionsPage = ({ setActivePage }: AssumptionsPageProps) => {
    const [modellingSectionOpen, setModellingSectionOpen] = useState(false);
    // Read once per render off the wall clock — the question is whether the figures are current NOW,
    // at the moment advice is being given, not on any date inside the projection.
    const ratesReview = ratesReviewStatus();

    const assumptionSections = buildAssumptionSections();
    return (
        <div className="max-w-4xl mx-auto">
             <button onClick={() => setActivePage('Dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-4">
                <ChevronLeft size={20} /> Back to Dashboard
            </button>
            <h1 className="text-4xl font-bold text-gray-900 mb-2"><GradientText>System Assumptions</GradientText></h1>
            <p className="text-lg text-gray-600 mb-8">This page outlines the key financial rates, thresholds, and assumptions used in the calculator's projections. Year 1 uses today's legislated rates and thresholds. Future years are projections and rely on assumptions about inflation and indexation. Actual government indexation and policy changes (and market returns) may be different, which can change your results.</p>

            {/* The client-facing half of the staleness safeguard. `ratesFreshness.test.ts` fails the
                build on the same date, but that only reaches a developer — and the failure mode here is
                quiet: an adviser writes an SOA a week after indexation, quotes last March's Age Pension
                figure, and nothing says otherwise. Both read `RATES_REVIEW_BY`, so one date moves them. */}
            {ratesReview.overdue && (
                <div role="status" className="mb-8 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">Rates may be out of date</p>
                    <p className="mt-1 text-sm text-slate-600">{ratesReview.notice}</p>
                </div>
            )}

            <div className="space-y-6">
                <Card>
                    <button
                        type="button"
                        onClick={() => setModellingSectionOpen((o) => !o)}
                        className="flex w-full items-center justify-between gap-3 text-left rounded-lg -mx-1 px-1 py-0.5 hover:bg-slate-50/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        aria-expanded={modellingSectionOpen}
                        aria-controls="assumptions-modelling-section"
                    >
                        <h2 id="assumptions-modelling-heading" className="text-xl font-bold text-gray-800">How financial modelling works in this tool</h2>
                        <ChevronDown
                            className={`shrink-0 text-gray-500 transition-transform duration-200 ${modellingSectionOpen ? 'rotate-180' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <div
                        id="assumptions-modelling-section"
                        role="region"
                        aria-labelledby="assumptions-modelling-heading"
                        aria-hidden={!modellingSectionOpen}
                        className={modellingSectionOpen ? 'mt-4' : 'hidden'}
                    >
                        <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                            <p>
                                Multi-year projections use <strong>Australian financial year</strong> periods from a <strong>projection start</strong>.
                                That start is the <strong>first day of the current calendar month</strong> (local time). Table rows follow <strong>financial years</strong>{' '}
                                (the first row may run from that start through <strong>30 June</strong>; later rows are <strong>full</strong> financial years).
                            </p>
                            <p>
                                <strong>Comparison Yr1</strong> is the label we use for the <strong>primary strategy comparison year</strong>.
                                For that comparison, the tool shows figures <strong>as they would look over a full financial year</strong>—<strong>as if</strong> your chosen{' '}
                                <strong>living arrangement</strong> and <strong>funding approach</strong> were already in place from <strong>1 July</strong> through <strong>30 June</strong>.
                                That gives a <strong>clearer, like-for-like</strong> view of what a normal year could look like <strong>after</strong> those decisions and the funding model are applied, even when you model a change part-way through the year.
                            </p>
                            <p>
                                Where the app offers it, an <strong>optional full financial year</strong> row gives a <strong>separate estimate</strong> that <strong>weights together pre-strategy and post-strategy assumptions</strong> across the <strong>same</strong> financial year
                                (for example when strategy costs or pension treatment change mid-year). Use it to understand <strong>timing</strong> between &quot;before&quot; and &quot;after&quot;; it is still an <strong>estimate</strong>, not a statutory financial-year total.
                            </p>
                            <p>
                                Unless noted elsewhere, the <strong>first projection period</strong> for rates on this page uses <strong>current</strong> pension settings, thresholds, and tax settings.{' '}
                                <strong>Later years</strong> assume <strong>indexation</strong> (for example pension and tax thresholds rising at the projection inflation rate below). <strong>Tax rates</strong> are generally held at today&apos;s legislative settings unless modelled otherwise.
                            </p>
                            <p className="text-gray-800 font-medium">All results are <strong>illustrative</strong> for strategic discussion.</p>
                        </div>
                    </div>
                </Card>
                {assumptionSections.map(section => (
                    <Card key={section.title}>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">{section.title}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            {section.items.map(item => (
                                <div key={item.label} className="flex justify-between text-sm py-2 border-b border-slate-200">
                                    <span className="text-gray-600 flex items-center">{item.label} {item.details && <InfoIcon text={item.details}/>}</span>
                                    <div className="text-right">
                                        <span className="font-semibold text-gray-800">{item.value}</span>
                                        {item.note && <p className="text-xs text-gray-500">{item.note}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default AssumptionsPage;
